/**
 * CrestBadge — visualises a user's hierarchical crest with optional
 * lineage (King + Ambassador above them).
 *
 * Renders nothing for plain users who aren't in a downline.
 *
 * Hits: GET /api/me/crest
 *
 * Created Feb 12 2026 for the King/Ambassador hierarchy rollout.
 */
import React, { useEffect, useState } from 'react';
import { Crown, Sparkles, UserPlus } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_THEMES = {
  admin:      { c: 'border-red-500/40 bg-red-500/10 text-red-200',         icon: Crown, label: 'ADMIN' },
  king:       { c: 'border-amber-500/40 bg-amber-500/10 text-amber-200',   icon: Crown, label: 'KING' },
  ambassador: { c: 'border-pink-500/40 bg-pink-500/10 text-pink-200',      icon: Sparkles, label: 'AMBASSADOR' },
  user:       { c: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200',      icon: UserPlus, label: 'RECRUIT' },
};

const CrestBadge = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/me/crest`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch {
        /* silent — badge stays hidden */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data?.crest_id) return null;

  const role = (data.role || 'user').toLowerCase();
  const theme = ROLE_THEMES[role] || ROLE_THEMES.user;
  const Icon = theme.icon;

  return (
    <div
      className={`rounded-xl border ${theme.c} ${compact ? 'p-2.5' : 'p-3'} flex items-center gap-3`}
      data-testid="crest-badge"
    >
      <div className="w-9 h-9 rounded-lg bg-black/30 grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest opacity-80" data-testid="crest-role-label">
          {theme.label}
        </p>
        <p className="text-base font-bold tracking-wider tabular-nums truncate" data-testid="crest-id">
          {data.crest_id}
        </p>
        {(data.king || data.ambassador) && (
          <p className="text-[11px] opacity-80 truncate" data-testid="crest-lineage">
            {data.king && (
              <span>
                <Crown className="w-3 h-3 inline -mt-0.5 mr-1" />
                Crest of {data.king.name}
                {data.king.number ? ` (#${data.king.number})` : ''}
              </span>
            )}
            {data.ambassador && (
              <>
                {data.king && <span className="mx-1 opacity-50">·</span>}
                <span>
                  via {data.ambassador.name}
                  {data.ambassador.number ? ` (#${data.ambassador.number})` : ''}
                </span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default CrestBadge;
